<template>
    <div
        :class="{
            'py-4': true,
            'pl-4': true,
            'pr-4': single,
            'kpmi-item': true,
        }"
    >
        <div :class="`kpmi-fields-area ${containerClass}`">
            <div v-if="showItemField" :class="`kpmi-item-field ${itemClass}`">
                <slot name="default" :value="item[itemName]" :input="itemChanged">
                    <v-text-field
                        :value="item[itemName]"
                        dense
                        outlined
                        hide-details="auto"
                        clearable
                        @input="itemChanged"
                    />
                </slot>
            </div>
            <div v-if="showTextField" :class="`kpmi-text-field ${textClass}`">
                <slot name="text" :value="item[textName]" :input="textChanged">
                    <v-textarea
                        :value="item[textName]"
                        rows="3"
                        dense
                        outlined
                        hide-details="auto"
                        @input="textChanged"
                    />
                </slot>
            </div>
            <div v-if="showTypeField" :class="`kpmi-type-field ${typeClass}`">
                <slot name="type" :value="item[typeName]" :input="typeChanged">
                    <v-text-field
                        :value="item[typeName]"
                        dense
                        outlined
                        hide-details="auto"
                        clearable
                        @input="typeChanged"
                    />
                </slot>
            </div>
        </div>
        <div v-if="!single" :class="`kpmi-action-area ${actionClass}`">
            <IconBtn icon-cls="trash" icon-color="grey" @click="$emit('remove-item', index)" />
            <IconBtn icon-cls="new" icon-color="grey" @click="$emit('add-item', index)" />
        </div>
    </div>
</template>

<script>
/**
 * A component with up to three input fields.
 * Displays data from an object (value) in the input fields and stores any changes made.
 *
 * Fires the following events:
 * - remove: `remove-item`: (index)
 * - add: `add-item`: (index)
 * - change: `input`: (index, value)
 *
 * There are three slots to customize the three fields:
 * - text
 * - default
 * - type
 * All slots receive the value and a input change function as slot properties.
 */
export default {
    name: 'MultiItemItem',
    props: {
        /**
         * Object with values to display.
         */
        value: {
            type: Object,
            default: null,
        },
        /**
         * Boolean indicating if container should be limited to 1 item.
         */
        single: {
            type: Boolean,
            default: false,
        },
        /**
         * Name of the text field (1st field). This is used as the property in the value object.
         */
        textName: {
            type: String,
            default: 'text',
        },
        /**
         * Name of the type field (2rd field). This is used as the property in the value object.
         */
        typeName: {
            type: String,
            default: 'type',
        },
        /**
         * Name of the item field (3nd field). This is used as the property in the value object.
         */
        itemName: {
            type: String,
            default: 'item',
        },
        /**
         * Show the text field (1st field).
         */
        showTextField: {
            type: Boolean,
            default: true,
        },
        /**
         * Show the type field (2rd field).
         */
        showTypeField: {
            type: Boolean,
            default: true,
        },
        /**
         * Show the item field (3nd field).
         */
        showItemField: {
            type: Boolean,
            default: true,
        },
        /**
         * Index to identify which item was edited. Will be sent with the `remove-item` & `add-item` events.
         */
        index: {
            type: Number,
            default: null,
        },
        /** Additional class string to be set on the field container div */
        containerClass: {
            type: String,
            default: '',
        },
        /** Additional class string to be set on the text slot div (1st field) */
        textClass: {
            type: String,
            default: '',
        },
        /** Additional class string to be set on the type slot div (2rd field) */
        typeClass: {
            type: String,
            default: '',
        },
        /** Additional class string to be set on the item slot div (3nd field) */
        itemClass: {
            type: String,
            default: '',
        },
        /** Additional class string to be set on the action container div */
        actionClass: {
            type: String,
            default: '',
        },
    },
    computed: {
        item() {
            const val = this.value || {};
            if (this.showTextField) {
                val[this.textName] = val[this.textName] || null;
            }
            if (this.showTypeField) {
                val[this.typeName] = val[this.typeName] || null;
            }
            if (this.showItemField) {
                val[this.itemName] = val[this.itemName] || null;
            }
            return val;
        },
    },
    methods: {
        textChanged(value) {
            const val = this.item;
            val[this.textName] = value;
            this.updateValue(val);
        },
        typeChanged(value) {
            const val = this.item;
            val[this.typeName] = value;
            this.updateValue(val);
        },
        itemChanged(value) {
            const val = this.item;
            val[this.itemName] = value;
            this.updateValue(val);
        },
        updateValue(value) {
            this.$emit('input', this.index, value);
        },
    },
};
</script>

<style lang="scss" scoped>
.kpmi-item {
    display: grid;
    grid-template-areas: 'fields action';
    grid-template-rows: auto;
    grid-template-columns: 1fr auto;

    :deep(.v-input) {
        background-color: white;
    }

    .kpmi-fields-area {
        grid-area: fields;
    }

    .kpmi-action-area {
        grid-area: action;
        background-color: var(--toolbar-bg-color);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }
}
</style>
